import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, request } from '@/api/client';
import type { ApplicationReceipt, PublicApplicationOptions, Vacancy } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { FilePicker } from '@/components/ui/FilePicker';
import { Input } from '@/components/ui/Input';
import { Select, Textarea } from '@/components/ui/Select';
import { APPLYING_FOR_OPTIONS } from '@/features/candidates/labels';
import { APPLICANT_DETAIL_FIELDS } from '../applicationFields';
import styles from '../pages/PublicJobsPage.module.css';

export function PublicApplicationForm({ job }: { job: Vacancy }) {
  const [department, setDepartment] = useState('');
  const [position, setPosition] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const options = useQuery({
    queryKey: ['public-application-options'],
    queryFn: () =>
      request<PublicApplicationOptions>('/api/v1/public/jobs/application-options', {
        anonymous: true,
      }),
    enabled: moreOpen,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<ApplicationReceipt | null>(null);
  const [locked, setLocked] = useState(false);
  const pending = useRef<FormData | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (!pending.current) {
      const form = new FormData(event.currentTarget);
      const textValue = (key: string) => {
        const value = form.get(key);
        return typeof value === 'string' ? value.trim() : '';
      };
      const file =
        event.currentTarget.querySelector<HTMLInputElement>('input[name="cv"]')?.files?.[0];
      const passport =
        event.currentTarget.querySelector<HTMLInputElement>('input[name="passport"]')
          ?.files?.[0];
      if (!(file instanceof File) || file.size === 0) {
        setError('Upload your CV before submitting your application.');
        return;
      }
      if (
        (file instanceof File ? file.size : 0) +
          (passport instanceof File ? passport.size : 0) >
        10 * 1024 * 1024
      ) {
        setError('Keep the combined CV and passport size under 10 MB.');
        return;
      }
      const data = new FormData();
      data.set(
        'application',
        JSON.stringify({
          request_id: crypto.randomUUID(),
          ...Object.fromEntries(
            ['full_name', 'email', 'phone', 'city', 'country', 'message', 'website'].map(
              (key) => [key, form.get(key) ?? ''],
            ),
          ),
          applying_for: textValue('applying_for') || 'placement',
          ...Object.fromEntries(
            ['department_id', 'preferred_position_id', 'preferred_country_id'].map((key) => [
              key,
              textValue(key) || null,
            ]),
          ),
          details: Object.fromEntries(
            APPLICANT_DETAIL_FIELDS.map(({ name, type }) => {
              const value = textValue(name);
              return [name, type === 'number' ? (value === '' ? null : Number(value)) : value];
            }),
          ),
        }),
      );
      if (file instanceof File && file.size) data.set('cv', file);
      if (passport instanceof File && passport.size) data.set('passport', passport);
      pending.current = data;
    }
    setBusy(true);
    setLocked(true);
    try {
      setReceipt(
        await request<ApplicationReceipt>(`/api/v1/public/jobs/${job.id}/applications`, {
          anonymous: true,
          method: 'POST',
          formData: pending.current,
        }),
      );
    } catch (problem) {
      const uncertain =
        !(problem instanceof ApiError) || problem.status === 0 || problem.status >= 500;
      setError(
        `${problem instanceof ApiError ? problem.message : 'The result could not be confirmed.'}${uncertain ? ' Use Check application to safely retry the same submission.' : ''}`,
      );
      if (!uncertain) {
        pending.current = null;
        setLocked(false);
      }
    } finally {
      setBusy(false);
    }
  };
  if (receipt)
    return (
      <section className={styles.form} aria-live="polite">
        <span className={styles.eyebrow}>APPLICATION RECEIVED</span>
        <h2>Thank you for applying.</h2>
        <p>
          Your application for <strong>{job.title}</strong> is with the Flyst recruitment team.
        </p>
        <p>You can close this page. Please keep this reference for any follow-up:</p>
        <code className={styles.receipt}>{receipt.id}</code>
        <Link to="/jobs">Explore more opportunities →</Link>
      </section>
    );
  return (
    <form
      className={styles.form}
      onInvalid={(event) => {
        if (event.currentTarget.querySelector('details')?.contains(event.target as Node))
          setMoreOpen(true);
      }}
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <h2>Apply for this role</h2>
      <p>Tell us about yourself. No account needed.</p>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <fieldset disabled={locked} className={styles.fields}>
        <legend className={styles.sectionTitle}>Basic details</legend>
        <Field label="Full name" required>
          {(ids) => (
            <Input {...ids} name="full_name" autoComplete="name" required maxLength={200} />
          )}
        </Field>
        <Field label="Email address" required>
          {(ids) => (
            <Input
              {...ids}
              name="email"
              autoComplete="email"
              type="email"
              required
              maxLength={320}
            />
          )}
        </Field>
        <Field label="Phone number" required hint="Include your country code.">
          {(ids) => (
            <Input
              {...ids}
              name="phone"
              autoComplete="tel"
              type="tel"
              required
              minLength={7}
              maxLength={40}
            />
          )}
        </Field>
        <div className={styles.pair}>
          <Field label="Current city">
            {(ids) => (
              <Input {...ids} name="city" autoComplete="address-level2" maxLength={120} />
            )}
          </Field>
          <Field label="Current country" required>
            {(ids) => (
              <Input
                {...ids}
                name="country"
                autoComplete="country-name"
                required
                maxLength={120}
              />
            )}
          </Field>
        </div>
        <Field
          label="CV / Resume"
          required
          hint="Required · PDF, Word or image · up to 10 MB including any passport upload"
        >
          {({ id, describedBy }) => (
            <FilePicker
              id={id}
              aria-describedby={describedBy}
              name="cv"
              required
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
            />
          )}
        </Field>
        <Field label="Message" hint="Briefly tell us about your experience.">
          {({ id }) => <textarea id={id} name="message" rows={4} maxLength={4000} />}
        </Field>
        <details
          className={styles.moreDetails}
          open={moreOpen}
          onToggle={(event) => {
            setMoreOpen(event.currentTarget.open);
          }}
        >
          <summary>
            More details <span>Optional · help us understand your background</span>
          </summary>
          <div className={styles.fields}>
            <h3 className={styles.sectionTitle}>Job preferences</h3>
            <Field label="Applying for">
              {(ids) => (
                <Select
                  {...ids}
                  name="applying_for"
                  defaultValue="placement"
                  options={APPLYING_FOR_OPTIONS}
                />
              )}
            </Field>
            {options.isError ? (
              <Alert tone="warning">
                Job preferences could not be loaded. You can still apply, or{' '}
                <button
                  type="button"
                  onClick={() => {
                    void options.refetch();
                  }}
                >
                  try again
                </button>
                .
              </Alert>
            ) : null}
            {options.isPending && moreOpen ? (
              <p className={styles.note}>Loading job preferences…</p>
            ) : null}
            <Field label="Preferred department">
              {(ids) => (
                <Select
                  {...ids}
                  name="department_id"
                  value={department}
                  disabled={!options.data}
                  placeholder="No preference"
                  options={(options.data?.departments ?? []).map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  onChange={(event) => {
                    setDepartment(event.target.value);
                    setPosition('');
                  }}
                />
              )}
            </Field>
            <Field label="Preferred position" hint="Choose a department to see its positions.">
              {(ids) => (
                <Select
                  {...ids}
                  name="preferred_position_id"
                  value={position}
                  disabled={!options.data}
                  placeholder="No preference"
                  options={(options.data?.positions ?? [])
                    .filter((item) => !item.department_id || item.department_id === department)
                    .map((item) => ({ value: item.id, label: item.name }))}
                  onChange={(event) => {
                    setPosition(event.target.value);
                  }}
                />
              )}
            </Field>
            <Field label="Preferred country">
              {(ids) => (
                <Select
                  {...ids}
                  name="preferred_country_id"
                  disabled={!options.data}
                  placeholder="No preference"
                  options={(options.data?.countries ?? []).map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                />
              )}
            </Field>
            <h3 className={styles.sectionTitle}>Education and experience</h3>
            {APPLICANT_DETAIL_FIELDS.map(({ name, label, type, maxLength, hint }) => (
              <Field key={name} label={label} hint={hint}>
                {(ids) =>
                  type === 'textarea' ? (
                    <Textarea {...ids} name={name} rows={3} maxLength={maxLength} />
                  ) : (
                    <Input
                      {...ids}
                      name={name}
                      type={type === 'number' ? 'number' : 'text'}
                      min={type === 'number' ? 0 : undefined}
                      max={type === 'number' ? 80 : undefined}
                      step={type === 'number' ? 'any' : undefined}
                      maxLength={maxLength}
                    />
                  )
                }
              </Field>
            ))}
            <h3 className={styles.sectionTitle}>Additional document</h3>
            <Field
              label="Passport"
              hint="Optional · PDF, Word or image · CV and passport together must be under 10 MB. You can provide this later."
            >
              {({ id, describedBy }) => (
                <FilePicker
                  id={id}
                  aria-describedby={describedBy}
                  name="passport"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                />
              )}
            </Field>
          </div>
        </details>
        <div hidden aria-hidden="true">
          <label>
            Website
            <input name="website" tabIndex={-1} autoComplete="off" />
          </label>
        </div>
        <label className={styles.consent}>
          <input type="checkbox" required />I agree to share these details with Flyst for this
          job application.
        </label>
      </fieldset>
      <Button type="submit" loading={busy} disabled={busy}>
        {locked ? 'Check application' : 'Submit application'}
      </Button>
      <p className={styles.note}>
        Your details and documents are shared privately with the recruitment team.
      </p>
    </form>
  );
}
